import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CryptoPrice, useCryptoStore } from '../../store/cryptoStore';
import CryptoChart from './CryptoChart';
import { useAuth } from '../../contexts/AuthContext';

interface TickerCardProps {
  data?: CryptoPrice;
}

const TickerCard: React.FC<TickerCardProps> = ({ data }) => {
  const [showChart, setShowChart] = useState(false);
  const { favorites, toggleFavorite, initializeFavorites, setSelectedChart } = useCryptoStore();
  const { isAuthenticated } = useAuth();
  
  useEffect(() => {
    if (isAuthenticated) {
      initializeFavorites();
    }
  }, [isAuthenticated, initializeFavorites]);

  if (!data) return null;

  const isFavorite = favorites.includes(data.symbol + 'USDT');

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (!isAuthenticated) {
        alert('Favorilere eklemek için giriş yapmalısınız!');
        return;
      }
      await toggleFavorite(data.symbol + 'USDT', data.symbol);
    } catch (error) {
      console.error('Favori işlemi sırasında hata:', error);
      alert('Favori işlemi sırasında bir hata oluştu');
    }
  };

  const handleChartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedChart(data.symbol);
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1000000000) {
      return `$${(volume / 1000000000).toFixed(2)}B`;
    }
    if (volume >= 1000000) {
      return `$${(volume / 1000000).toFixed(2)}M`;
    }
    return `$${volume.toFixed(2)}`;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <Symbol>
            {data.symbol}
            <SymbolUsdt>USDT</SymbolUsdt>
          </Symbol>
          <ButtonGroup>
            <ChartButton 
              onClick={handleChartClick}
              title="Grafiği Göster"
            >
              📈
            </ChartButton>
            <FavoriteButton 
              onClick={handleFavoriteClick}
              $isFavorite={isFavorite}
              title={isFavorite ? 'Favorilerden Çıkar' : 'Favorilere Ekle'}
              disabled={!isAuthenticated}
            >
              {isFavorite ? '★' : '☆'}
            </FavoriteButton>
          </ButtonGroup>
        </CardHeader>

        <Price $isUp={data.priceChange >= 0}>
          ${Number(data.price).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6
          })}
        </Price>

        <Change $isUp={data.priceChange >= 0}>
          {data.priceChange >= 0 ? '↑' : '↓'} 
          {Math.abs(data.priceChange).toFixed(2)}%
        </Change>

        <Volume>
          Hacim: {formatVolume(data.volume)}
        </Volume>

        <LastUpdate>
          Son güncelleme: {new Date(data.lastUpdate).toLocaleTimeString()}
        </LastUpdate>
      </Card>

      {showChart && (
        <CryptoChart 
          symbol={data.symbol} 
          onClose={() => setShowChart(false)} 
        />
      )}
    </>
  );
};

const Card = styled.div`
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(to right, #3B82F6, #60A5FA);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Symbol = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SymbolUsdt = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: normal;
`;

const ChartButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    color: #3B82F6;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FavoriteButton = styled.button<{ $isFavorite: boolean }>`
  background: none;
  border: none;
  color: ${props => props.$isFavorite ? '#FFD700' : 'rgba(255, 255, 255, 0.5)'};
  font-size: 1.5rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  padding: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: ${props => props.disabled ? 'none' : 'scale(1.1)'};
    color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.5)' : '#FFD700'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.95)'};
  }
`;

const Price = styled.div<{ $isUp: boolean }>`
  font-size: 1.5rem;
  color: ${props => props.$isUp ? '#4CAF50' : '#F44336'};
  font-weight: 500;
  transition: color 0.3s ease;
`;

const Change = styled.div<{ $isUp: boolean }>`
  color: ${props => props.$isUp ? '#4CAF50' : '#F44336'};
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.3s ease;
`;

const Volume = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const LastUpdate = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  text-align: right;
  margin-top: 4px;
`;

export default TickerCard;